import { createContext, useEffect, type FC, type ReactNode } from 'react';

import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { useAccount } from 'shared/hooks/use-web3';
import { useRoomStore } from 'shared/store/roomStore';
import { useUIStore } from 'shared/store/uiStore';

import { audioManager } from './audio-manager';
import { AUDIO_TYPES } from './audio.types';

export const AudioContext = createContext(audioManager);

export const AudioProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const { isConnected } = useAccount();
  const { roomStatus } = useRoomStore();
  const isAudioMuted = useUIStore((s) => s.sound.isAudioMuted);

  useEffect(() => {
    audioManager.init(isAudioMuted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Initialize audio manager only once

  useEffect(() => {
    if (!isConnected) return;

    const handleFirstInteraction = () => {
      if (
        roomStatus === RoomStatusEnum.ReadyForGame ||
        roomStatus === RoomStatusEnum.ReadyForReveal
      ) {
        audioManager.loadSounds([AUDIO_TYPES.GAME_BACKGROUND]);
        audioManager.playMusic(AUDIO_TYPES.GAME_BACKGROUND);
      } else {
        audioManager.loadSounds([AUDIO_TYPES.LOBBY_BACKGROUND]);
        audioManager.playMusic(AUDIO_TYPES.LOBBY_BACKGROUND);
      }
      window.removeEventListener('click', handleFirstInteraction);
      window.removeEventListener('touch', handleFirstInteraction);
    };

    window.addEventListener('click', handleFirstInteraction, { once: true });
    window.addEventListener('touch', handleFirstInteraction, { once: true });
  }, [isConnected, roomStatus]);

  return <AudioContext.Provider value={audioManager}>{children}</AudioContext.Provider>;
};
